import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cn, formatDate, formatCurrency } from "@/lib/utils";
import { CalendarIcon, Check, FileText, Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";

type HistoricalRenewalData = {
  id: number;
  licenseId: number;
  renewalDate: Date;
  amount: number;
  notes: string;
  invoiceFile?: File | null;
  hasInvoice: boolean; // Changed from optional to required
};

type EditHistoricalRenewalDialogProps = {
  renewal: HistoricalRenewalData;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedRenewal: HistoricalRenewalData) => void;
};

export default function EditHistoricalRenewalDialog({
  renewal,
  isOpen,
  onClose,
  onSave,
}: EditHistoricalRenewalDialogProps) {
  const [renewalData, setRenewalData] = useState<HistoricalRenewalData>({
    ...renewal,
    invoiceFile: null,
  });
  
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>("Existing invoice");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: updateRenewal, isPending } = useMutation({
    mutationFn: async (data: HistoricalRenewalData) => {
      const formData = new FormData();
      formData.append('renewalDate', data.renewalDate.toISOString());
      formData.append('previousCost', data.amount.toString());
      formData.append('newCost', data.amount.toString());
      formData.append('currency', 'USD');
      formData.append('notes', data.notes || '');
      
      // Handle the invoice file if provided
      if (data.invoiceFile) {
        formData.append('invoice', data.invoiceFile);
      }
      
      const response = await fetch(`/api/historical-renewals/${data.id}`, {
        method: 'PUT',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update renewal');
      }
      
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', renewal.licenseId, 'historical-renewals'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', renewal.licenseId] });
      toast({
        title: "Renewal updated",
        description: "The renewal record has been updated successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update renewal",
        description: error.message || "There was an error updating the renewal record. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB.",
          variant: "destructive",
        });
        return;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF, PNG, or JPG file.",
          variant: "destructive",
        });
        return;
      }
      
      // Save the file name to display it in the UI
      setCurrentFileName(file.name);
      setUploadedFile(file);
      setRenewalData(prev => ({ ...prev, invoiceFile: file, hasInvoice: true }));
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    if (!renewalData.renewalDate) {
      toast({
        title: "Missing date",
        description: "Please select a renewal date.",
        variant: "destructive",
      });
      return;
    }
    
    if (renewalData.amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount greater than zero.",
        variant: "destructive",
      });
      return;
    }
    
    updateRenewal(renewalData);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => !isPending && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Historical Renewal</DialogTitle>
          <DialogDescription>
            Update this renewal record from {formatDate(renewal.renewalDate)}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="renewalDate">Renewal Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !renewalData.renewalDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {renewalData.renewalDate ? (
                      formatDate(renewalData.renewalDate)
                    ) : (
                      <span>Select date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={renewalData.renewalDate}
                    onSelect={(date) => date && setRenewalData(prev => ({ ...prev, renewalDate: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="amount"
                  type="text"
                  className="pl-7"
                  placeholder="0.00"
                  value={renewalData.amount || ''}
                  onChange={(e) => {
                    // Only allow numbers and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    setRenewalData(prev => ({ ...prev, amount: parseFloat(formatted) || 0 }));
                    e.target.value = formatted;
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional information about this renewal..."
              value={renewalData.notes}
              onChange={(e) => setRenewalData(prev => ({ ...prev, notes: e.target.value }))}
              className="h-20"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Invoice (Optional)</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-md p-4">
              {renewalData.hasInvoice && !uploadedFile ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-500" />
                    <span className="text-sm font-medium">{currentFileName}</span>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Open the invoice preview in the same way the View Invoice dialog does
                        // This ensures consistency between both views
                        toast({
                          title: "Opening invoice",
                          description: `Viewing ${currentFileName}`,
                        });
                        // In a real app, this would trigger an API call to get the document
                        // For demo purposes, we're simulating this by forcing the ViewInvoiceDialog to show
                        // We'd pass the actual file here from state
                        window.open(`#/invoice-preview?renewal=${renewal.id}&filename=${encodeURIComponent(currentFileName)}`);
                      }}
                    >
                      View
                    </Button>
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setRenewalData(prev => ({ ...prev, hasInvoice: false }));
                        toast({
                          title: "Invoice removed",
                          description: "The existing invoice has been removed. Upload a new one or save without an invoice.",
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : uploadedFile ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-500" />
                    <span className="text-sm font-medium">{uploadedFile.name}</span>
                  </div>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setUploadedFile(null);
                      setRenewalData(prev => ({ ...prev, invoiceFile: null, hasInvoice: false }));
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center mb-2">
                    <Upload className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm text-center text-slate-600 mb-2">
                    Drag and drop or click to upload
                  </p>
                  <p className="text-xs text-center text-slate-400 mb-3">
                    Supports PDF, PNG, JPG (max 10MB)
                  </p>
                  <div className="flex justify-center">
                    <input
                      type="file"
                      id="invoice-upload-edit"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('invoice-upload-edit')?.click()}
                    >
                      Select File
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}